import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Pause, Play, X, Wind, Activity, Sparkles, Target } from 'lucide-react';
import ControlPause from './ControlPause';
import Reset from './Reset';
import HtmlModuleFrame from './HtmlModuleFrame';
import { PA_DRILLS } from '@/config/drillsCatalog';
import { markDrillSeen, getSeenMap } from '@/lib/drillProgress';

/**
 * FlowGrid — ported from flow-grid-3.html
 *
 * Six screens: entry → wait (30s) → grid (60s) ×10 → breath (4×5s ×3 rounds) → journal (5min) → complete.
 * Canvas-based grid path and box-breath ball animations.
 */

const TOTAL_SESSIONS = 10;
const SESSION_SECS = 60;
const WAIT_SECS = 30;
const ROWS = 5;
const COLS = 5;
const CLAY = '#C45A3B';
const CREAM = '#F5F2EB';
const TOTAL_SEGS = TOTAL_SESSIONS * 2 + 1;
const CIRC = 2 * Math.PI * 30;

// Tools — user picks one or more of these before selecting movements.
// Each session then randomly picks a tool from the user's selected pool. Long-bar
// exercises are constrained to a level-3 path (or centered dot for motorboat).
const TOOLS = [
  {
    id: 'empty-hand',
    name: 'Empty hand',
    short: 'Hand',
    desc: 'No implement — pure body coordination.',
  },
  {
    id: 'mace',
    name: 'Mace',
    short: 'Mace',
    desc: 'Steel mace or club — offset weight amplifies the path.',
  },
  {
    id: 'long-bar',
    name: 'Long bar',
    short: 'Bar',
    desc: 'Long bar locks the path to level 3 (motorboat stays centered).',
  },
];

// Simple SVG icon for each tool — inline so no external assets are needed.
const ToolIcon = ({ id, className, stroke = '#F5F2EB' }) => {
  if (id === 'empty-hand') {
    return (
      <svg className={className} viewBox="0 0 32 32" fill="none">
        <path d="M12 20V9a2 2 0 1 1 4 0v8" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
        <path d="M16 17V7a2 2 0 1 1 4 0v10" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
        <path d="M20 17v-6a2 2 0 1 1 4 0v10c0 4.4-3.6 8-8 8s-8-3.6-8-8v-6a2 2 0 1 1 4 0v3" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (id === 'mace') {
    return (
      <svg className={className} viewBox="0 0 32 32" fill="none">
        <circle cx="22" cy="9" r="5" stroke={stroke} strokeWidth="1.8" />
        <path d="M19 12l-13 13" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
        <path d="M6 25l-2 2" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (id === 'long-bar') {
    return (
      <svg className={className} viewBox="0 0 32 32" fill="none">
        <rect x="2" y="14" width="28" height="4" rx="1.5" stroke={stroke} strokeWidth="1.8" />
        <line x1="9" y1="10" x2="9" y2="22" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
        <line x1="23" y1="10" x2="23" y2="22" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  return null;
};

const MODES = [
  { id: 'level-up', group: 'Level ride', name: 'Level ride up', desc: 'Snakes left to right across each level, from L1 up to L5.' },
  { id: 'level-down', group: 'Level ride', name: 'Level ride down', desc: 'Snakes from L5 down to L1, reversing direction each level.' },
  { id: 'chamber-right', group: 'Chamber ride', name: 'Chamber ride right', desc: 'Snakes vertically through chambers from C1 across to C5.' },
  { id: 'chamber-left', group: 'Chamber ride', name: 'Chamber ride left', desc: 'Snakes vertically through chambers from C5 back to C1.' },
  { id: 'chamber-lock', group: 'Chamber lock', name: 'Chamber lock', desc: 'Stays in one chamber the whole minute, oscillating slowly through all levels.' },
  { id: 'level-lock', group: 'Level lock', name: 'Level lock', desc: 'Stays on one level the whole minute, moving slowly through all chambers.' },
  { id: 'x-factor', group: 'X Factor', name: 'X Factor', desc: 'Travels diagonally corner to corner — four 15-second legs tracing an X.' },
];

// Movement demo GIFs — one per session, shown during the 30s waiting room so the user
// knows the physical motion to perform. Assets live in the project's asset bucket.
const CDN_MOVE = 'https://customer-assets.emergentagent.com/job_9ef24d93-e44e-4ca5-96a4-258599e1230b/artifacts';
const MOVEMENTS = [
  { id: 'motorboat',   name: 'Motorboat',        gif: `${CDN_MOVE}/9zm9xa2r_Motorboat.gif` },
  { id: 'steer-l',     name: 'Steer left',       gif: `${CDN_MOVE}/7f3mzdtp_Steer-L.gif` },
  { id: 'steer-r',     name: 'Steer right',      gif: `${CDN_MOVE}/uh3j2rut_Steer-R.gif` },
  { id: 'roll-fwd',    name: 'Roll forward',     gif: `${CDN_MOVE}/sj8ihbrt_Roll-Fwd.gif` },
  { id: 'roll-back',   name: 'Roll back',        gif: `${CDN_MOVE}/r37g9ft4_Roll-Back.gif` },
  { id: 'row-fwd',     name: 'Row forward',      gif: `${CDN_MOVE}/kbvew0tt_Row-Fwd.gif` },
  { id: 'row-bck',     name: 'Row back',         gif: `${CDN_MOVE}/d0x00o0r_Row-Bck.gif` },
  { id: 'swim',        name: 'Swim',             gif: `${CDN_MOVE}/6wi0kdj9_Swim.gif` },
  { id: 'figure8-lh',  name: 'Figure 8 · left',  gif: `${CDN_MOVE}/5yem1cbm_Figure8-LH.gif` },
  { id: 'figure8-rh',  name: 'Figure 8 · right', gif: `${CDN_MOVE}/2mmn11ci_Figure8-RH.gif` },
];

const CORNERS = ['L1C1', 'L5C1', 'L1C5', 'L5C5'];
const CDESC = {
  L1C1: 'L1, C1 — bottom left',
  L5C1: 'L5, C1 — top left',
  L1C5: 'L1, C5 — bottom right',
  L5C5: 'L5, C5 — top right',
};

const PHASES = [
  { label: 'in', prefix: 'Breathe', secs: 5, type: 'inhale' },
  { label: 'Hold', secs: 5, type: 'hold-in' },
  { label: 'out', prefix: 'Breathe', secs: 5, type: 'exhale' },
  { label: 'Hold', secs: 5, type: 'hold-out' },
];
const TOTAL_ROUNDS = 3;

const SCREEN = {
  CHOICE: 'choice',
  ENTRY: 'entry',
  TOOLS: 'tools',
  MOVES: 'moves',
  WAIT: 'wait',
  GRID: 'grid',
  BREATH: 'breath',
  COMPLETE: 'complete',
  BREATHWORK: 'breathwork',
  RESET: 'reset',
};

const WAIT_GIF_SECS = 15;   // first half of the waiting room — movement demo GIF
const WAIT_PREV_SECS = 15;  // second half of the waiting room — path preview animation

const lr = (l) => 5 - l;

function buildPath(mode, lockSub, xCorner) {
  const pts = [];
  if (mode === 'level-up') {
    for (let lv = 1; lv <= 5; lv++) {
      const row = lr(lv);
      const ltr = (lv - 1) % 2 === 0;
      for (let ci = 0; ci < 5; ci++) pts.push([row, ltr ? ci : 4 - ci]);
    }
  } else if (mode === 'level-down') {
    for (let lv = 5; lv >= 1; lv--) {
      const row = lr(lv);
      const ltr = (5 - lv) % 2 === 0;
      for (let ci = 0; ci < 5; ci++) pts.push([row, ltr ? ci : 4 - ci]);
    }
  } else if (mode === 'chamber-right') {
    for (let ch = 1; ch <= 5; ch++) {
      const col = ch - 1;
      const up = (ch - 1) % 2 === 0;
      for (let li = 0; li < 5; li++) {
        const lv = up ? li + 1 : 5 - li;
        pts.push([lr(lv), col]);
      }
    }
  } else if (mode === 'chamber-left') {
    for (let ch = 5; ch >= 1; ch--) {
      const col = ch - 1;
      const up = (5 - ch) % 2 === 0;
      for (let li = 0; li < 5; li++) {
        const lv = up ? li + 1 : 5 - li;
        pts.push([lr(lv), col]);
      }
    }
  } else if (mode === 'chamber-lock') {
    const col = lockSub - 1;
    for (let i = 0; i <= 240; i++) {
      const t = i / 240;
      const ph = (t * 6) % 2;
      const lv = ph < 1 ? 1 + ph * 4 : 5 - (ph - 1) * 4;
      pts.push([lr(lv), col]);
    }
  } else if (mode === 'level-lock') {
    const row = lr(lockSub);
    for (let i = 0; i <= 240; i++) {
      const t = i / 240;
      const ph = (t * 6) % 2;
      const ch = ph < 1 ? 1 + ph * 4 : 5 - (ph - 1) * 4;
      pts.push([row, ch - 1]);
    }
  } else if (mode === 'center-lock') {
    // Long-bar + motorboat: stays locked at Level 3, Chamber 3 (row 2, col 2).
    for (let i = 0; i <= 240; i++) pts.push([2, 2]);
  } else if (mode === 'x-factor') {
    const C = { L1C1: [lr(1), 0], L5C1: [lr(5), 0], L1C5: [lr(1), 4], L5C5: [lr(5), 4] };
    const O = { L1C1: 'L5C5', L5C5: 'L1C1', L5C1: 'L1C5', L1C5: 'L5C1' };
    const S = 60;
    let cur = xCorner;
    for (let leg = 0; leg < 4; leg++) {
      const nxt = O[cur];
      const [r0, c0] = C[cur];
      const [r1, c1] = C[nxt];
      for (let s = 0; s <= S; s++) {
        const t = s / S;
        pts.push([r0 + (r1 - r0) * t, c0 + (c1 - c0) * t]);
      }
      cur = nxt;
    }
  }
  return pts;
}

function posLabel(mode, lockSub, xCorner) {
  if (mode === 'level-up') return 'L1, C1 — bottom left';
  if (mode === 'level-down') return 'L5, C1 — top left';
  if (mode === 'chamber-right') return 'C1, L1 — bottom left';
  if (mode === 'chamber-left') return 'C5, L1 — bottom right';
  if (mode === 'chamber-lock') return `C${lockSub}, L1 — bottom of column`;
  if (mode === 'level-lock') return `L${lockSub}, C1 — left of level`;
  if (mode === 'center-lock') return 'L3, C3 — dead center';
  if (mode === 'x-factor') return CDESC[xCorner];
  return '—';
}

function easeIO(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

function pickMode() {
  return MODES[Math.floor(Math.random() * MODES.length)];
}

function pickLockSub() {
  return Math.floor(Math.random() * 5) + 1;
}

function pickXCorner() {
  return CORNERS[Math.floor(Math.random() * 4)];
}

const FlowGrid = () => {
  const navigate = useNavigate();
  const [screen, setScreen] = useState(SCREEN.CHOICE);
  // Drill currently being viewed (from PA_DRILLS) — null means the choice screen is showing.
  const [activeDrill, setActiveDrill] = useState(null);
  // Snapshot of the "seen" map, refreshed each time we return to the choice screen.
  const [seenMap, setSeenMap] = useState(() => getSeenMap());
  const [sessionIdx, setSessionIdx] = useState(0);
  const [config, setConfig] = useState({ mode: MODES[0], lockSub: 1, xCorner: 'L1C1', movement: MOVEMENTS[0], tool: TOOLS[0] });
  const [waitRemaining, setWaitRemaining] = useState(WAIT_SECS);
  const [waitPhase, setWaitPhase] = useState(0); // 0 = movement GIF, 1 = path preview
  const [gridRemaining, setGridRemaining] = useState(SESSION_SECS);
  const [overallPct, setOverallPct] = useState(0);
  const [breathRound, setBreathRound] = useState(0);
  const [breathPhase, setBreathPhase] = useState(0);
  const [breathRem, setBreathRem] = useState(5);
  const [breathDone, setBreathDone] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // Move list — user selects which movements to include before the workout begins.
  // Defaults to all movements so the flow works out of the box.
  const [selectedMoveIds, setSelectedMoveIds] = useState(MOVEMENTS.map((m) => m.id));
  // Tool list — user picks which tools rotate through their workout.
  // Defaults to all three so the flow works even if they skip the step.
  const [selectedToolIds, setSelectedToolIds] = useState(TOOLS.map((t) => t.id));
  // Pre-computed per-session tool sequence (10 entries) — prevents streaks and
  // lets the wait-room preview the upcoming tool.
  const [sessionTools, setSessionTools] = useState([]);

  const gridCanvasRef = useRef(null);
  const breathCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const rafRef = useRef(null);
  const startTsRef = useRef(null);
  const tickRef = useRef(null);            // current running RAF tick — captured so Resume can restart it
  const pausedElapsedRef = useRef(0);      // elapsed time captured at the moment of pause
  const pathRef = useRef([]);
  const sessionIdxRef = useRef(0);
  const configRef = useRef(config);
  const screenRef = useRef(screen);
  const selectedMoveIdsRef = useRef(selectedMoveIds);
  const selectedToolIdsRef = useRef(selectedToolIds);
  const sessionToolsRef = useRef(sessionTools);

  useEffect(() => {
    sessionIdxRef.current = sessionIdx;
  }, [sessionIdx]);
  useEffect(() => {
    configRef.current = config;
  }, [config]);
  useEffect(() => {
    screenRef.current = screen;
  }, [screen]);
  useEffect(() => {
    selectedMoveIdsRef.current = selectedMoveIds;
  }, [selectedMoveIds]);
  useEffect(() => {
    selectedToolIdsRef.current = selectedToolIds;
  }, [selectedToolIds]);
  useEffect(() => {
    sessionToolsRef.current = sessionTools;
  }, [sessionTools]);

  const getOverallPct = (si, phase, lp) => {
    const done = si * 2 + (phase === 'grid' ? 1 : 0);
    return done / TOTAL_SEGS + lp / TOTAL_SEGS;
  };

  // ---------- GRID CANVAS ----------
  const resizeGrid = useCallback(() => {
    const c = gridCanvasRef.current;
    if (!c) return;
    const parent = c.parentElement;
    const W = parent.clientWidth;
    const H = parent.clientHeight;
    const dpr = window.devicePixelRatio || 1;
    c.width = W * dpr;
    c.height = H * dpr;
    c.style.width = W + 'px';
    c.style.height = H + 'px';
    const ctx = c.getContext('2d');
    ctx.resetTransform();
    ctx.scale(dpr, dpr);
  }, []);

  const drawGrid = useCallback((pct) => {
    const c = gridCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    const W = c.clientWidth;
    const H = c.clientHeight;
    const cw = W / COLS;
    const ch = H / ROWS;
    const GAP = 4;
    const R = 9;
    ctx.clearRect(0, 0, W, H);
    for (let r = 0; r < ROWS; r++) {
      for (let col = 0; col < COLS; col++) {
        const x = col * cw + GAP;
        const y = r * ch + GAP;
        const w = cw - GAP * 2;
        const h = ch - GAP * 2;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(x, y, w, h, R);
        else ctx.rect(x, y, w, h);
        ctx.fillStyle = 'rgba(245,242,235,0.035)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(245,242,235,0.07)';
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
    }
    const path = pathRef.current;
    if (!path.length || pct <= 0) return;

    const n = path.length;
    const fi = pct * (n - 1);
    const i0 = Math.min(Math.floor(fi), n - 2);
    const fr = fi - i0;
    const [r0, c0] = path[i0];
    const [r1, c1] = path[Math.min(i0 + 1, n - 1)];
    const head = {
      x: (c0 + (c1 - c0) * fr) * cw + cw / 2,
      y: (r0 + (r1 - r0) * fr) * ch + ch / 2,
      i: i0,
    };
    const iH = head.i;
    const mode = configRef.current.mode.id;
    const isLock = mode === 'chamber-lock' || mode === 'level-lock';
    const isX = mode === 'x-factor';

    if (!isLock && !isX) {
      for (let i = 0; i <= iH; i++) {
        const [rr, cc] = path[i];
        const a = Math.max(0.04, 0.44 - (iH - i) * 0.04);
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(cc * cw + GAP, rr * ch + GAP, cw - GAP * 2, ch - GAP * 2, R);
        else ctx.rect(cc * cw + GAP, rr * ch + GAP, cw - GAP * 2, ch - GAP * 2);
        ctx.fillStyle = `rgba(45,59,45,${a})`;
        ctx.fill();
      }
    }
    const TL = isX ? 61 : isLock ? 24 : path.length;
    const ts = Math.max(0, iH - TL + 1);
    if (iH > 0) {
      ctx.beginPath();
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      const [sr, sc] = path[ts];
      ctx.moveTo(sc * cw + cw / 2, sr * ch + ch / 2);
      for (let i = ts + 1; i <= iH; i++) {
        const [rr, cc] = path[i];
        ctx.lineTo(cc * cw + cw / 2, rr * ch + ch / 2);
      }
      ctx.lineTo(head.x, head.y);
      ctx.strokeStyle = 'rgba(196,90,59,0.45)';
      ctx.lineWidth = isX ? 2.5 : 3.5;
      ctx.stroke();
    }
    for (let i = ts; i < iH; i++) {
      const [rr, cc] = path[i];
      const a = Math.max(0.05, 0.46 - (iH - i) * (isLock ? 0.022 : 0.055));
      ctx.beginPath();
      ctx.arc(cc * cw + cw / 2, rr * ch + ch / 2, 4, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(196,90,59,${a})`;
      ctx.fill();
    }
    if (isX && pct > 0) {
      const ll = Math.floor(path.length / 4);
      const done = Math.floor(iH / ll);
      for (let leg = 0; leg < done; leg++) {
        const s = leg * ll;
        const e = s + ll;
        if (e >= path.length) continue;
        ctx.beginPath();
        ctx.moveTo(path[s][1] * cw + cw / 2, path[s][0] * ch + ch / 2);
        ctx.lineTo(path[e][1] * cw + cw / 2, path[e][0] * ch + ch / 2);
        ctx.strokeStyle = 'rgba(196,90,59,0.12)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    }
    const hR = Math.min(cw, ch) * 0.16;
    ctx.beginPath();
    ctx.arc(head.x, head.y, hR + 6, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(196,90,59,0.10)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(head.x, head.y, hR, 0, Math.PI * 2);
    ctx.fillStyle = CLAY;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(head.x, head.y, hR * 0.36, 0, Math.PI * 2);
    ctx.fillStyle = CREAM;
    ctx.fill();
  }, []);

  // ---------- BREATH CANVAS ----------
  const resizeBreath = useCallback(() => {
    const c = breathCanvasRef.current;
    if (!c) return;
    const parent = c.parentElement;
    const W = parent.clientWidth;
    const H = parent.clientHeight;
    const dpr = window.devicePixelRatio || 1;
    c.width = W * dpr;
    c.height = H * dpr;
    c.style.width = W + 'px';
    c.style.height = H + 'px';
    const ctx = c.getContext('2d');
    ctx.resetTransform();
    ctx.scale(dpr, dpr);
  }, []);

  const drawBreath = useCallback((type, pct) => {
    const c = breathCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    const W = c.clientWidth;
    const H = c.clientHeight;
    const cx = W / 2;
    const cy = H / 2;
    const maxR = Math.min(W, H) * 0.44;
    const minR = maxR * 0.42;
    ctx.clearRect(0, 0, W, H);
    let radius;
    if (type === 'inhale') radius = minR + (maxR - minR) * easeIO(pct);
    else if (type === 'exhale') radius = maxR - (maxR - minR) * easeIO(pct);
    else if (type === 'hold-in') radius = maxR;
    else radius = minR;
    const throb = type === 'hold-in' || type === 'hold-out' ? Math.sin(pct * Math.PI * 4) * (type === 'hold-in' ? 4 : 2) : 0;
    const r = radius + throb;
    ctx.beginPath();
    ctx.arc(cx, cy, r + 18, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(196,90,59,0.07)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, r + 8, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(196,90,59,0.11)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = CLAY;
    ctx.fill();
    const alpha =
      type === 'inhale'
        ? 0.3 + pct * 0.4
        : type === 'exhale'
        ? 0.7 - pct * 0.4
        : type === 'hold-in'
        ? 0.68
        : 0.32;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.35, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(245,242,235,${alpha})`;
    ctx.fill();
  }, []);

  // ---------- FLOW CONTROL ----------
  const cancelRaf = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  // ---------- PATH PREVIEW CANVAS (played during the second half of the waiting room) ----------
  const drawPreview = useCallback((p) => {
    const c = previewCanvasRef.current;
    if (!c) return;
    const path = pathRef.current;
    if (!path || path.length === 0) return;
    const parent = c.parentElement;
    if (!parent) return;
    const dpr = window.devicePixelRatio || 1;
    const W = parent.clientWidth;
    const H = parent.clientHeight;
    if (c.width !== W * dpr || c.height !== H * dpr) {
      c.width = W * dpr;
      c.height = H * dpr;
      c.style.width = `${W}px`;
      c.style.height = `${H}px`;
      const ctx0 = c.getContext('2d');
      ctx0.resetTransform();
      ctx0.scale(dpr, dpr);
    }
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    // 5x5 grid layout
    const pad = 12;
    const cw = (W - pad * 2) / 5;
    const ch = (H - pad * 2) / 5;
    const cellRadius = Math.min(cw, ch) * 0.16;
    // background dots for each cell
    for (let r = 0; r < 5; r++) {
      for (let cc = 0; cc < 5; cc++) {
        ctx.beginPath();
        ctx.arc(pad + cc * cw + cw / 2, pad + r * ch + ch / 2, cellRadius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(245,242,235,0.08)';
        ctx.fill();
      }
    }
    // Determine how much of the path to draw
    const iH = Math.min(path.length - 1, Math.floor(p * (path.length - 1)));
    // Draw trail
    if (iH > 0) {
      ctx.beginPath();
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      const [sr, sc] = path[0];
      ctx.moveTo(pad + sc * cw + cw / 2, pad + sr * ch + ch / 2);
      for (let i = 1; i <= iH; i++) {
        const [rr, cc] = path[i];
        ctx.lineTo(pad + cc * cw + cw / 2, pad + rr * ch + ch / 2);
      }
      ctx.strokeStyle = 'rgba(196,90,59,0.6)';
      ctx.lineWidth = 3;
      ctx.stroke();
    }
    // Head dot
    const [hr, hc] = path[iH];
    ctx.beginPath();
    ctx.arc(pad + hc * cw + cw / 2, pad + hr * ch + ch / 2, cellRadius * 1.6, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(196,90,59,0.18)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(pad + hc * cw + cw / 2, pad + hr * ch + ch / 2, cellRadius * 0.9, 0, Math.PI * 2);
    ctx.fillStyle = CLAY;
    ctx.fill();
  }, []);

  const startWait = useCallback(() => {
    let m = pickMode();
    let ls = pickLockSub();
    const xc = pickXCorner();
    // Pick a movement from the user's selected list (fall back to all if empty)
    const pool = MOVEMENTS.filter((mv) => selectedMoveIdsRef.current.includes(mv.id));
    const src = pool.length > 0 ? pool : MOVEMENTS;
    const mv = src[sessionIdxRef.current % src.length];
    // Pick the tool for this session from the pre-built sequence (fallback to
    // a fresh pick if the sequence hasn't been built for some reason).
    let toolId = sessionToolsRef.current[sessionIdxRef.current];
    if (!toolId) {
      const tp = selectedToolIdsRef.current.length > 0 ? selectedToolIdsRef.current : TOOLS.map((t) => t.id);
      toolId = tp[Math.floor(Math.random() * tp.length)];
    }
    const tool = TOOLS.find((t) => t.id === toolId) || TOOLS[0];
    // Long-bar override: any exercise except motorboat locks to level-3;
    // motorboat locks to the dead-center dot (L3, C3).
    if (tool.id === 'long-bar') {
      if (mv.id === 'motorboat') {
        m = { id: 'center-lock', group: 'Long bar', name: 'Center lock', desc: 'Long bar with motorboat — stay locked at level 3, chamber 3.' };
      } else {
        m = { id: 'level-lock', group: 'Level lock', name: 'Level 3 ride', desc: 'Long bar constrains the path to level 3 — move slowly through all chambers.' };
        ls = 3;
      }
    }
    setConfig({ mode: m, lockSub: ls, xCorner: xc, movement: mv, tool });
    // Pre-build the path so the preview canvas can animate it during the second half of the wait
    pathRef.current = buildPath(m.id, ls, xc);
    setScreen(SCREEN.WAIT);
    setWaitRemaining(WAIT_SECS);
    setWaitPhase(0);
    startTsRef.current = performance.now();
    const tick = (now) => {
      const el = (now - startTsRef.current) / 1000;
      const pct = Math.min(el / WAIT_SECS, 1);
      const rem = Math.max(0, WAIT_SECS - el);
      setWaitRemaining(rem);
      // First 15s → phase 0 (GIF). Next 15s → phase 1 (path preview animation).
      const phase = el < WAIT_GIF_SECS ? 0 : 1;
      setWaitPhase(phase);
      setOverallPct(getOverallPct(sessionIdxRef.current, 'wait', pct));
      if (phase === 1) {
        // Drive the preview animation based on the second-half progress
        const p = Math.min((el - WAIT_GIF_SECS) / WAIT_PREV_SECS, 1);
        drawPreview(p);
      }
      if (pct < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        beginSession();
      }
    };
    tickRef.current = tick;
    rafRef.current = requestAnimationFrame(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const beginSession = useCallback(() => {
    cancelRaf();
    setScreen(SCREEN.GRID);
    const cfg = configRef.current;
    pathRef.current = buildPath(cfg.mode.id, cfg.lockSub, cfg.xCorner);
    setGridRemaining(SESSION_SECS);
    setTimeout(() => {
      resizeGrid();
      drawGrid(0);
      startTsRef.current = performance.now();
      const tick = (now) => {
        const el = (now - startTsRef.current) / 1000;
        const pct = Math.min(el / SESSION_SECS, 1);
        const rem = Math.max(0, SESSION_SECS - el);
        setGridRemaining(rem);
        setOverallPct(getOverallPct(sessionIdxRef.current, 'grid', pct));
        drawGrid(pct);
        if (pct < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          const next = sessionIdxRef.current + 1;
          setSessionIdx(next);
          setTimeout(() => {
            if (next < TOTAL_SESSIONS) startWait();
            else beginBreath();
          }, 600);
        }
      };
      tickRef.current = tick;
      rafRef.current = requestAnimationFrame(tick);
    }, 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawGrid, resizeGrid]);

  const beginBreath = useCallback(() => {
    cancelRaf();
    setScreen(SCREEN.BREATH);
    setBreathRound(0);
    setBreathPhase(0);
    setBreathDone(false);
    setTimeout(() => {
      resizeBreath();
      startBreathPhase(0, 0);
    }, 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizeBreath]);

  const startBreathPhase = useCallback(
    (round, phaseIdx) => {
      setBreathRound(round);
      setBreathPhase(phaseIdx);
      const phase = PHASES[phaseIdx];
      setBreathRem(phase.secs);
      startTsRef.current = performance.now();
      const tick = (now) => {
        const el = (now - startTsRef.current) / 1000;
        const pct = Math.min(el / phase.secs, 1);
        const rem = Math.max(0, phase.secs - el);
        setBreathRem(rem);
        drawBreath(phase.type, pct);
        if (pct < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          let nextPhase = phaseIdx + 1;
          let nextRound = round;
          if (nextPhase >= PHASES.length) {
            nextPhase = 0;
            nextRound = round + 1;
          }
          if (nextRound >= TOTAL_ROUNDS) {
            drawBreath('exhale', 1);
            setBreathDone(true);
          } else {
            startBreathPhase(nextRound, nextPhase);
          }
        }
      };
      tickRef.current = tick;
      rafRef.current = requestAnimationFrame(tick);
    },
    [drawBreath]
  );

  const restart = () => {
    cancelRaf();
    setSessionIdx(0);
    setBreathRound(0);
    setBreathPhase(0);
    setBreathDone(false);
    setOverallPct(0);
    setIsPaused(false);
    setScreen(SCREEN.ENTRY);
  };

  // ---------- PAUSE / RESUME / EXIT ----------
  // The three workout screens (WAIT, GRID, BREATH) can be paused mid-tick.
  // We capture the current elapsed time, cancel the RAF, and restart the same tick
  // with an adjusted startTsRef on resume so timing is exactly preserved.
  const pauseWorkout = useCallback(() => {
    if (!tickRef.current || isPaused) return;
    const s = screenRef.current;
    if (s !== SCREEN.WAIT && s !== SCREEN.GRID && s !== SCREEN.BREATH) return;
    pausedElapsedRef.current = (performance.now() - startTsRef.current) / 1000;
    cancelRaf();
    setIsPaused(true);
  }, [isPaused]);

  const resumeWorkout = useCallback(() => {
    if (!tickRef.current || !isPaused) return;
    startTsRef.current = performance.now() - pausedElapsedRef.current * 1000;
    setIsPaused(false);
    rafRef.current = requestAnimationFrame(tickRef.current);
  }, [isPaused]);

  const exitWorkout = useCallback(() => {
    cancelRaf();
    tickRef.current = null;
    pausedElapsedRef.current = 0;
    setIsPaused(false);
    setSessionIdx(0);
    setBreathRound(0);
    setBreathPhase(0);
    setBreathDone(false);
    setOverallPct(0);
    setScreen(SCREEN.ENTRY);
    navigate('/client');
  }, [navigate]);

  // Spacebar toggles pause during any of the timed workout screens
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== 'Space') return;
      const s = screenRef.current;
      if (s !== SCREEN.WAIT && s !== SCREEN.GRID && s !== SCREEN.BREATH) return;
      // Don't hijack space if the user is typing in an input
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      if (isPaused) resumeWorkout();
      else pauseWorkout();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPaused, pauseWorkout, resumeWorkout]);

  const handleBegin = () => {
    setSessionIdx(0);
    setOverallPct(0);
    // Show tool-select first, then move-select before the workout begins
    setScreen(SCREEN.TOOLS);
  };

  const toggleTool = (id) => {
    setSelectedToolIds((prev) => {
      if (prev.includes(id)) {
        if (prev.length === 1) return prev; // keep at least one tool
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
  };

  // Build a 10-session tool sequence from the selected pool that avoids two-in-a-row
  // repeats whenever the user has picked more than one tool.
  const buildToolSequence = (pool, count = TOTAL_SESSIONS) => {
    if (pool.length === 0) return Array(count).fill(TOOLS[0].id);
    const seq = [];
    let prev = null;
    for (let i = 0; i < count; i++) {
      const options = pool.length > 1 ? pool.filter((t) => t !== prev) : pool;
      const pick = options[Math.floor(Math.random() * options.length)];
      seq.push(pick);
      prev = pick;
    }
    return seq;
  };

  const handleToolsContinue = () => {
    if (selectedToolIds.length === 0) return;
    setSessionTools(buildToolSequence(selectedToolIds));
    setScreen(SCREEN.MOVES);
  };

  const handleMovesContinue = () => {
    if (selectedMoveIds.length === 0) return;
    startWait();
  };

  const toggleMove = (id) => {
    setSelectedMoveIds((prev) => {
      if (prev.includes(id)) {
        // Don't allow removing the last one
        if (prev.length === 1) return prev;
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => cancelRaf();
  }, []);

  // Resize listeners
  useEffect(() => {
    const onResize = () => {
      if (screenRef.current === SCREEN.GRID) {
        resizeGrid();
      }
      if (screenRef.current === SCREEN.BREATH) {
        resizeBreath();
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [resizeGrid, resizeBreath]);

  // Helpers for display
  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const renderEmphasised = (name) => {
    const parts = name.split(' ');
    const last = parts.pop();
    return (
      <>
        {parts.join(' ')} <em className="italic">{last}</em>
      </>
    );
  };

  // Shared pause+exit control cluster shown on WAIT, GRID, BREATH screens
  const workoutControls = (
    <div className="fixed top-4 right-4 z-40 flex items-center gap-2">
      <button
        onClick={isPaused ? resumeWorkout : pauseWorkout}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#F5F2EB]/15 bg-[#1A1814]/70 backdrop-blur-sm text-[#F5F2EB]/70 hover:text-[#F5F2EB] hover:border-[#F5F2EB]/35 transition-colors text-xs"
        data-testid="workout-pause-btn"
        title={isPaused ? 'Resume workout (Space)' : 'Pause workout (Space)'}
      >
        {isPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{isPaused ? 'Resume' : 'Pause'}</span>
      </button>
      <button
        onClick={exitWorkout}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#F5F2EB]/15 bg-[#1A1814]/70 backdrop-blur-sm text-[#F5F2EB]/60 hover:text-[#C45A3B] hover:border-[#C45A3B]/50 transition-colors text-xs"
        data-testid="workout-exit-btn"
        title="Exit workout"
      >
        <X className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Exit</span>
      </button>
    </div>
  );

  const pauseOverlay = isPaused ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A1814]/80 backdrop-blur-md"
      data-testid="pause-overlay"
    >
      <div className="max-w-sm w-full mx-6 text-center rounded-2xl bg-[#252018] border border-[#F5F2EB]/[0.1] p-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#F5F2EB]/65 mb-2">Workout paused</div>
        <h3 className="font-serif text-3xl text-[#F5F2EB] mb-6">
          Take your <em className="italic text-[#C45A3B]">time.</em>
        </h3>
        <p className="text-[13px] text-[#F5F2EB]/55 leading-relaxed mb-7">
          The timer will pick up exactly where you left off.
        </p>
        <div className="flex flex-col gap-3">
          <button
            onClick={resumeWorkout}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#F5F2EB] text-[#1A1814] font-medium hover:bg-[#F5F2EB]/90 transition-colors"
            data-testid="pause-resume-btn"
          >
            <Play className="w-4 h-4" /> Resume
          </button>
          <button
            onClick={exitWorkout}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#F5F2EB]/15 text-[#F5F2EB]/75 hover:text-[#F5F2EB] hover:border-[#F5F2EB]/35 transition-colors text-sm"
            data-testid="pause-exit-btn"
          >
            <X className="w-4 h-4" /> Exit workout
          </button>
        </div>
        <div className="text-[10px] text-[#F5F2EB]/60 mt-6 tracking-[0.15em] uppercase">Press space to resume</div>
      </div>
    </div>
  ) : null;

  // -------------------------- RENDER --------------------------
  if (activeDrill) {
    return (
      <HtmlModuleFrame
        src={`/modules/${activeDrill.file}`}
        title={activeDrill.title}
        onExit={() => {
          setSeenMap(getSeenMap());
          setActiveDrill(null);
        }}
        testId={`pa-drill-${activeDrill.id}`}
      />
    );
  }

  if (screen === SCREEN.RESET) {
    return <Reset onExit={() => setScreen(SCREEN.CHOICE)} />;
  }

  if (screen === SCREEN.BREATHWORK) {
    return <ControlPause onBack={() => setScreen(SCREEN.CHOICE)} />;
  }

  if (screen === SCREEN.CHOICE) {
    return (
      <div className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex flex-col overflow-y-auto">
        <button
          onClick={() => navigate('/client/hub')}
          className="absolute top-safe-6 left-6 flex items-center gap-2 text-[#F5F2EB]/65 hover:text-[#F5F2EB]/80 transition-colors z-10"
          data-testid="flowgrid-choice-back"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
        <div className="flex-1 flex items-start justify-center p-6 py-12 sm:py-16">
          <div className="max-w-[860px] w-full text-center">
            <div className="text-[11px] font-medium tracking-[0.25em] uppercase text-[#F5F2EB]/65 mb-4">
              The Body
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl mb-4 leading-[1.05] text-[#F5F2EB]">
              Choose your
              <br />
              <em className="italic text-[#C45A3B]">practice.</em>
            </h1>
            <p className="text-[#F5F2EB]/60 text-sm mb-10 max-w-[460px] mx-auto leading-relaxed">
              Reset the nervous system, move your body through coordinates, or
              train your breath tolerance with a Control Pause session.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <button
                onClick={() => setScreen(SCREEN.RESET)}
                className="group text-left bg-[#252018] rounded-2xl p-6 border border-[#F5F2EB]/[0.07] hover:border-[#D6A85A]/60 hover:bg-[#2C2620] transition-all"
                data-testid="flowgrid-choose-reset"
              >
                <div className="w-11 h-11 rounded-xl bg-[#D6A85A]/15 border border-[#D6A85A]/30 flex items-center justify-center mb-4">
                  <Sparkles className="w-5 h-5 text-[#D6A85A]" />
                </div>
                <div className="font-serif text-2xl text-[#F5F2EB] mb-1">Reset</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#F5F2EB]/50 mb-3">Nervous system · short</div>
                <div className="text-sm text-[#F5F2EB]/60 leading-relaxed">
                  A short guided reset — drop the shoulders, unclench the jaw, come back to the room.
                </div>
                <div className="mt-4 inline-flex items-center gap-1 text-[#D6A85A] text-sm font-medium">
                  Begin <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </button>

              <button
                onClick={() => setScreen(SCREEN.ENTRY)}
                className="group text-left bg-[#252018] rounded-2xl p-6 border border-[#F5F2EB]/[0.07] hover:border-[#C45A3B]/60 hover:bg-[#2C2620] transition-all"
                data-testid="flowgrid-choose-workout"
              >
                <div className="w-11 h-11 rounded-xl bg-[#C45A3B]/15 border border-[#C45A3B]/30 flex items-center justify-center mb-4">
                  <Activity className="w-5 h-5 text-[#C45A3B]" />
                </div>
                <div className="font-serif text-2xl text-[#F5F2EB] mb-1">Physical workout</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#F5F2EB]/50 mb-3">Flow grid · 15 min</div>
                <div className="text-sm text-[#F5F2EB]/60 leading-relaxed">
                  10 sessions of body movement across chambers and levels, plus box-breath cool-down.
                </div>
                <div className="mt-4 inline-flex items-center gap-1 text-[#C45A3B] text-sm font-medium">
                  Begin <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </button>

              <button
                onClick={() => setScreen(SCREEN.BREATHWORK)}
                className="group text-left bg-[#252018] rounded-2xl p-6 border border-[#F5F2EB]/[0.07] hover:border-[#86AC93]/60 hover:bg-[#2C2620] transition-all"
                data-testid="flowgrid-choose-breathwork"
              >
                <div className="w-11 h-11 rounded-xl bg-[#86AC93]/15 border border-[#86AC93]/30 flex items-center justify-center mb-4">
                  <Wind className="w-5 h-5 text-[#86AC93]" />
                </div>
                <div className="font-serif text-2xl text-[#F5F2EB] mb-1">Breathwork</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#F5F2EB]/50 mb-3">Control Pause · 10 min</div>
                <div className="text-sm text-[#F5F2EB]/60 leading-relaxed">
                  Buteyko breath-hold trainer. Measure CO2 tolerance, track your band, build calm.
                </div>
                <div className="mt-4 inline-flex items-center gap-1 text-[#86AC93] text-sm font-medium">
                  Begin <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </button>
            </div>

            {/* Body & tone drills — short standalone modules */}
            <div className="mt-4 text-left">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-4 h-4 text-[#C45A3B]" />
                <div className="text-[11px] font-medium tracking-[0.22em] uppercase text-[#F5F2EB]/60">
                  Body & tone drills
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {PA_DRILLS.map((d) => {
                  const seen = Boolean(seenMap[`pa-${d.id}`]);
                  return (
                    <button
                      key={d.id}
                      onClick={() => {
                        markDrillSeen(`pa-${d.id}`);
                        setSeenMap(getSeenMap());
                        setActiveDrill(d);
                      }}
                      className="relative text-left bg-[#1F1B14] rounded-xl p-4 border border-[#F5F2EB]/[0.06] hover:border-[#C45A3B]/50 hover:bg-[#2C2620] transition-all"
                      data-testid={`flowgrid-drill-${d.id}`}
                    >
                      {seen && (
                        <span
                          className="absolute top-3 right-3 text-[9px] tracking-[0.15em] uppercase text-[#F5F2EB]/65 bg-[#F5F2EB]/[0.06] border border-[#F5F2EB]/10 rounded-full px-2 py-0.5"
                          data-testid={`flowgrid-drill-${d.id}-seen`}
                        >
                          Seen
                        </span>
                      )}
                      <div className="font-serif text-[17px] text-[#F5F2EB] mb-1 pr-12">{d.title}</div>
                      <div className="text-[12.5px] text-[#F5F2EB]/55 leading-snug">{d.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (screen === SCREEN.ENTRY) {
    return (
      <div className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex flex-col">
        <button
          onClick={() => setScreen(SCREEN.CHOICE)}
          className="absolute top-safe-6 left-6 flex items-center gap-2 text-[#F5F2EB]/65 hover:text-[#F5F2EB]/80 transition-colors z-10"
          data-testid="flowgrid-back"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-[520px] text-center">
            <div className="text-[11px] font-medium tracking-[0.25em] uppercase text-[#F5F2EB]/65 mb-4">
              Flow grid
            </div>
            <h1 className="font-serif text-6xl sm:text-7xl mb-10 leading-[1.05] text-[#F5F2EB]">
              Fifteen
              <br />
              <em className="italic text-[#C45A3B]">minutes.</em>
            </h1>
            <div className="grid grid-cols-3 gap-4 mb-10">
              {[
                { val: '10', lbl: 'Sessions' },
                { val: '21′', lbl: 'Total' },
                { val: '7', lbl: 'Patterns' },
              ].map((s) => (
                <div key={s.lbl} className="bg-[#252018] rounded-2xl p-5 border border-[#F5F2EB]/[0.07]">
                  <div className="font-serif text-3xl text-[#F5F2EB] mb-1">{s.val}</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#F5F2EB]/50">{s.lbl}</div>
                </div>
              ))}
            </div>
            <button
              onClick={handleBegin}
              className="inline-flex items-center gap-2 px-9 py-4 rounded-full bg-[#F5F2EB] text-[#1A1814] font-medium hover:bg-[#F5F2EB]/90 transition-colors"
              data-testid="flowgrid-begin"
            >
              Begin
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-[#F5F2EB]/65 mt-5">+ box breath cool-down</p>
          </div>
        </div>
      </div>
    );
  }

  // TOOL SELECTION SCREEN — pick which tools rotate through the workout
  if (screen === SCREEN.TOOLS) {
    return (
      <div className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex flex-col">
        <button
          onClick={() => setScreen(SCREEN.ENTRY)}
          className="absolute top-safe-6 left-6 flex items-center gap-2 text-[#F5F2EB]/65 hover:text-[#F5F2EB]/80 transition-colors z-10"
          data-testid="tools-back"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-[720px] w-full">
            <div className="text-center mb-8">
              <div className="text-[10px] font-medium tracking-[0.25em] uppercase text-[#F5F2EB]/65 mb-2">
                Choose your tools
              </div>
              <h2 className="font-serif text-[32px] sm:text-4xl text-[#F5F2EB] leading-tight">
                What's <em className="italic text-[#C45A3B]">in your hand?</em>
              </h2>
              <p className="text-[12px] text-[#F5F2EB]/45 mt-3 max-w-[440px] mx-auto leading-relaxed">
                Pick one or more. Each session will randomly draw from your pool — and long bar
                will re-route the path to level 3.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              {TOOLS.map((t) => {
                const selected = selectedToolIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    onClick={() => toggleTool(t.id)}
                    aria-pressed={selected}
                    data-testid={`tool-${t.id}`}
                    className={`p-5 rounded-2xl border text-left transition-all bg-[#252018] hover:-translate-y-0.5 ${
                      selected
                        ? 'border-[#C45A3B] shadow-lg shadow-[#C45A3B]/10'
                        : 'border-[#F5F2EB]/[0.07] hover:border-[#F5F2EB]/25 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${selected ? 'bg-[#C45A3B]/15 border border-[#C45A3B]/40' : 'bg-[#1A1814] border border-[#F5F2EB]/10'}`}>
                        <ToolIcon id={t.id} className="w-6 h-6" stroke={selected ? '#C45A3B' : '#F5F2EB'} />
                      </div>
                      <div
                        className={`w-5 h-5 rounded-md flex items-center justify-center text-[11px] transition-colors ${
                          selected
                            ? 'bg-[#C45A3B] text-white border border-[#C45A3B]'
                            : 'border border-[#F5F2EB]/25 text-transparent'
                        }`}
                        aria-hidden="true"
                      >
                        ✓
                      </div>
                    </div>
                    <div className="font-serif text-xl text-[#F5F2EB] mb-1">{t.name}</div>
                    <div className="text-[12px] text-[#F5F2EB]/55 leading-relaxed">{t.desc}</div>
                  </button>
                );
              })}
            </div>

            <button
              onClick={handleToolsContinue}
              disabled={selectedToolIds.length === 0}
              className="w-full py-3.5 rounded-full bg-[#F5F2EB] text-[#1A1814] font-medium hover:bg-[#F5F2EB]/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="tools-continue"
            >
              Continue with {selectedToolIds.length} {selectedToolIds.length === 1 ? 'tool' : 'tools'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // MOVE SELECTION SCREEN — pick which movements the workout will rotate through
  if (screen === SCREEN.MOVES) {
    return (
      <div className="h-screen bg-[#1A1814] text-[#F5F2EB] flex flex-col overflow-hidden">
        <button
          onClick={() => setScreen(SCREEN.TOOLS)}
          className="absolute top-safe-6 left-6 flex items-center gap-2 text-[#F5F2EB]/65 hover:text-[#F5F2EB]/80 transition-colors z-10"
          data-testid="moves-back"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-[900px] w-full">
            <div className="text-center mb-6">
              <div className="text-[10px] font-medium tracking-[0.25em] uppercase text-[#F5F2EB]/65 mb-2">
                Choose your move list
              </div>
              <h2 className="font-serif text-[32px] sm:text-4xl text-[#F5F2EB] leading-tight">
                Which movements are <em className="italic text-[#C45A3B]">in play?</em>
              </h2>
              <p className="text-[11px] text-[#F5F2EB]/45 mt-2">
                Selected movements will rotate one-per-session across the {TOTAL_SESSIONS} rounds.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5">
              {MOVEMENTS.map((mv) => {
                const selected = selectedMoveIds.includes(mv.id);
                return (
                  <button
                    key={mv.id}
                    onClick={() => toggleMove(mv.id)}
                    aria-pressed={selected}
                    data-testid={`move-${mv.id}`}
                    className={`p-3 rounded-xl border transition-all bg-[#252018] hover:-translate-y-0.5 ${
                      selected
                        ? 'border-[#C45A3B] shadow-lg shadow-[#C45A3B]/10'
                        : 'border-[#F5F2EB]/[0.07] hover:border-[#F5F2EB]/25'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-serif text-[13.5px] text-[#F5F2EB] text-left leading-tight">
                        {mv.name}
                      </div>
                      <div
                        className={`w-4 h-4 rounded-md flex items-center justify-center text-[10px] transition-colors flex-shrink-0 ${
                          selected
                            ? 'bg-[#C45A3B] text-white border border-[#C45A3B]'
                            : 'border border-[#F5F2EB]/25 text-transparent'
                        }`}
                        aria-hidden="true"
                      >
                        ✓
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Session rotation preview */}
            <div className="rounded-2xl bg-[#252018] border border-[#F5F2EB]/[0.07] px-4 py-3 mb-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5F2EB]/65 mb-2">
                Session rotation
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Array.from({ length: TOTAL_SESSIONS }).map((_, i) => {
                  const pool = MOVEMENTS.filter((mv) => selectedMoveIds.includes(mv.id));
                  const src = pool.length > 0 ? pool : MOVEMENTS;
                  const mv = src[i % src.length];
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#1A1814] border border-[#F5F2EB]/[0.08]"
                    >
                      <span className="text-[10px] text-[#F5F2EB]/65 font-mono tabular-nums w-4 text-right">
                        {i + 1}
                      </span>
                      <span className="text-[11px] text-[#F5F2EB]/85">{mv.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              onClick={handleMovesContinue}
              disabled={selectedMoveIds.length === 0}
              className="w-full py-3.5 rounded-full bg-[#F5F2EB] text-[#1A1814] font-medium hover:bg-[#F5F2EB]/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="moves-continue"
            >
              Begin with {selectedMoveIds.length} {selectedMoveIds.length === 1 ? 'movement' : 'movements'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === SCREEN.WAIT) {
    const m = config.mode;
    const waitPct = (WAIT_SECS - waitRemaining) / WAIT_SECS;
    return (
      <div className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex flex-col">
        {workoutControls}
        {pauseOverlay}
        <div className="px-6 py-5 flex items-center justify-between border-b border-[#F5F2EB]/[0.07]">
          <div className="text-[#F5F2EB]/60 text-sm font-medium uppercase tracking-[0.15em]">
            Waiting room
          </div>
          <div className="text-sm text-[#F5F2EB]/65" data-testid="wait-session">
            {sessionIdx + 1} of {TOTAL_SESSIONS}
          </div>
        </div>
        {/* overall progress */}
        <div className="h-1 bg-[#F5F2EB]/8">
          <div className="h-full bg-[#C45A3B] transition-all duration-150" style={{ width: `${overallPct * 100}%` }} />
        </div>

        <div className="flex-1 flex flex-col justify-center items-center p-6">
          <div className="flex flex-wrap items-center gap-6 mb-6 justify-center max-w-full">
            <div className="relative w-[72px] h-[72px]">
              <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90">
                <circle cx="36" cy="36" r="30" stroke="rgba(245,242,235,0.1)" strokeWidth="3" fill="none" />
                <circle
                  cx="36"
                  cy="36"
                  r="30"
                  stroke={CLAY}
                  strokeWidth="3"
                  fill="none"
                  strokeDasharray={CIRC}
                  strokeDashoffset={CIRC * (1 - waitPct)}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center font-serif text-2xl text-[#F5F2EB]">
                {Math.ceil(waitRemaining)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#F5F2EB]/65 mb-1">Up next</div>
              <div className="font-serif text-2xl text-[#F5F2EB]" data-testid="wait-mode-name">
                {m.name}
              </div>
            </div>
            {config.tool && (
              <div
                className="ml-auto flex items-center gap-3 pl-6 border-l border-[#F5F2EB]/10"
                data-testid="wait-tool"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#252018] border border-[#C45A3B]/40 flex items-center justify-center">
                  <ToolIcon id={config.tool.id} className="w-7 h-7" stroke="#C45A3B" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5F2EB]/65 mb-0.5">Tool</div>
                  <div className="font-serif text-lg text-[#F5F2EB]" data-testid="wait-tool-name">
                    {config.tool.name}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Phase 0 (0-15s): Movement demo GIF */}
          {waitPhase === 0 && config.movement && (
            <div
              className="w-full max-w-md bg-[#252018] rounded-2xl border border-[#F5F2EB]/[0.07] overflow-hidden mb-5"
              data-testid="wait-movement-gif"
            >
              <div className="aspect-video bg-black flex items-center justify-center overflow-hidden">
                <img
                  src={config.movement.gif}
                  alt={config.movement.name}
                  className="w-full h-full object-contain"
                  draggable={false}
                />
              </div>
              <div className="px-5 py-3 flex items-center justify-between border-t border-[#F5F2EB]/[0.07]">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5F2EB]/65">Movement</div>
                  <div className="font-serif text-lg text-[#F5F2EB]" data-testid="wait-movement-name">
                    {config.movement.name}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5F2EB]/65">Session {sessionIdx + 1}</div>
              </div>
            </div>
          )}

          {/* Phase 1 (15-30s): Path preview animation */}
          {waitPhase === 1 && (
            <div
              className="w-full max-w-md bg-[#252018] rounded-2xl border border-[#F5F2EB]/[0.07] overflow-hidden mb-5"
              data-testid="wait-path-preview"
            >
              <div className="aspect-video relative">
                <canvas ref={previewCanvasRef} className="absolute inset-0 w-full h-full" />
              </div>
              <div className="px-5 py-3 flex items-center justify-between border-t border-[#F5F2EB]/[0.07]">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5F2EB]/65">Path preview</div>
                  <div className="font-serif text-lg text-[#F5F2EB]" data-testid="wait-path-name">
                    {m.name}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5F2EB]/65">Session {sessionIdx + 1}</div>
              </div>
            </div>
          )}

          <div className="bg-[#252018] rounded-2xl p-6 max-w-md w-full border border-[#F5F2EB]/[0.07]">
            <div
              className="inline-block text-[10px] uppercase tracking-[0.2em] text-[#C45A3B] mb-3 px-2 py-1 rounded-full"
              style={{ backgroundColor: 'rgba(196,90,59,0.12)' }}
            >
              {m.group}
            </div>
            <div className="font-serif text-3xl mb-3 text-[#F5F2EB] leading-tight">{renderEmphasised(m.name)}</div>
            <p className="text-sm text-[#F5F2EB]/65 leading-relaxed mb-5">{m.desc}</p>
            <div className="border-t border-[#F5F2EB]/10 pt-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#F5F2EB]/65 mb-1">Start position</div>
              <div className="text-sm text-[#F5F2EB]/85">{posLabel(m.id, config.lockSub, config.xCorner)}</div>
            </div>
          </div>
        </div>

        <div className="h-1 bg-[#F5F2EB]/8">
          <div className="h-full bg-[#F5F2EB]/40 transition-all duration-150" style={{ width: `${waitPct * 100}%` }} />
        </div>
      </div>
    );
  }

  if (screen === SCREEN.GRID) {
    const m = config.mode;
    const sessionPct = (SESSION_SECS - gridRemaining) / SESSION_SECS;
    return (
      <div className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex flex-col">
        {workoutControls}
        {pauseOverlay}
        <div className="px-6 py-5 flex items-center justify-between border-b border-[#F5F2EB]/[0.07]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5F2EB]/50">{m.group}</div>
            <div className="text-sm text-[#F5F2EB]/85 mt-0.5">{posLabel(m.id, config.lockSub, config.xCorner)}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-[#F5F2EB]/65">Session {sessionIdx + 1}</div>
            <div className="font-serif text-2xl text-[#F5F2EB]" data-testid="grid-timer">
              {fmt(gridRemaining)}
            </div>
          </div>
        </div>

        {/* session dots */}
        <div className="px-6 py-3 flex gap-1.5">
          {Array.from({ length: TOTAL_SESSIONS }).map((_, i) => {
            const done = i < sessionIdx;
            const active = i === sessionIdx;
            const fill = active ? sessionPct * 100 : done ? 100 : 0;
            return (
              <div key={i} className="flex-1 h-1 bg-[#F5F2EB]/8 rounded-full overflow-hidden">
                <div className="h-full bg-[#C45A3B] transition-all duration-100" style={{ width: `${fill}%` }} />
              </div>
            );
          })}
        </div>

        <div className="flex-1 px-4 pb-4 flex">
          <div className="flex-1 flex">
            {/* col labels top */}
            <div className="flex-1 flex flex-col">
              <div className="flex">
                <div className="w-8" />
                {['C1', 'C2', 'C3', 'C4', 'C5'].map((c) => (
                  <div key={c} className="flex-1 text-center text-[10px] uppercase tracking-wider text-[#F5F2EB]/65 py-1">
                    {c}
                  </div>
                ))}
              </div>
              <div className="flex flex-1">
                <div className="w-8 flex flex-col justify-around items-center text-[10px] uppercase tracking-wider text-[#F5F2EB]/65">
                  {['L5', 'L4', 'L3', 'L2', 'L1'].map((l) => (
                    <span key={l}>{l}</span>
                  ))}
                </div>
                <div className="flex-1 relative">
                  <canvas ref={gridCanvasRef} className="block w-full h-full" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-3 flex items-center gap-2 border-t border-[#F5F2EB]/[0.07]">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: sessionPct >= 1 ? '#5DCAA5' : CLAY }}
          />
          <span className="text-xs text-[#F5F2EB]/55">{sessionPct >= 1 ? 'Done' : 'Running'}</span>
        </div>
      </div>
    );
  }

  if (screen === SCREEN.BREATH) {
    const phase = PHASES[breathPhase];
    return (
      <div className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex flex-col">
        {workoutControls}
        {pauseOverlay}
        <div className="px-6 py-5 flex items-center justify-between border-b border-[#F5F2EB]/[0.07]">
          <div className="text-[#F5F2EB]/60 text-sm font-medium uppercase tracking-[0.15em]">
            Cool-down · Box breath
          </div>
          <div className="text-sm text-[#F5F2EB]/65">
            Round {breathRound + 1} of {TOTAL_ROUNDS}
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="font-serif text-3xl sm:text-4xl mb-8 text-[#F5F2EB]">
            {breathDone ? (
              <>
                Well <em className="italic text-[#C45A3B]">done.</em>
              </>
            ) : phase.prefix ? (
              <>
                {phase.prefix} <em className="italic text-[#C45A3B]">{phase.label}</em>
              </>
            ) : (
              <em className="italic text-[#C45A3B]">{phase.label}</em>
            )}
          </div>
          <div className="relative w-[280px] h-[280px] sm:w-[340px] sm:h-[340px]">
            <canvas ref={breathCanvasRef} className="block w-full h-full" />
            <div className="absolute inset-0 flex items-center justify-center text-2xl font-serif text-[#F5F2EB]">
              {!breathDone && Math.ceil(breathRem)}
            </div>
          </div>
        </div>

        <div className="px-6 py-6 flex flex-col items-center gap-4">
          <div className="flex gap-2">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i < breathPhase
                    ? 'bg-[#F5F2EB]/40'
                    : i === breathPhase && !breathDone
                    ? 'bg-[#C45A3B]'
                    : 'bg-[#F5F2EB]/10'
                }`}
              />
            ))}
          </div>
          <div className="flex gap-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`w-6 h-1 rounded-full transition-colors ${
                  breathDone ? 'bg-[#5DCAA5]' : i < breathRound ? 'bg-[#F5F2EB]/40' : i === breathRound ? 'bg-[#C45A3B]' : 'bg-[#F5F2EB]/10'
                }`}
              />
            ))}
          </div>
          {breathDone && (
            <button
              onClick={() => setScreen(SCREEN.COMPLETE)}
              className="mt-2 px-8 py-3 rounded-full bg-[#F5F2EB] text-[#1A1814] font-medium hover:bg-[#F5F2EB]/90 transition-colors"
              data-testid="breath-continue"
            >
              Finish workout
            </button>
          )}
        </div>
      </div>
    );
  }

  if (screen === SCREEN.COMPLETE) {
    return (
      <div className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex flex-col items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h2 className="font-serif text-6xl sm:text-7xl leading-[1.05] mb-8">
            Well
            <br />
            <em className="italic text-[#C45A3B]">done.</em>
          </h2>
          <p className="text-[#F5F2EB]/65 leading-relaxed mb-10">
            10 minutes of movement and a box-breath cool-down. Workout complete.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={restart}
              className="px-6 py-3 rounded-full border border-[#F5F2EB]/20 text-[#F5F2EB]/80 hover:border-[#F5F2EB]/45 hover:text-[#F5F2EB] transition-all"
              data-testid="flowgrid-restart"
            >
              Start again
            </button>
            <button
              onClick={() => navigate('/client/hub')}
              className="px-6 py-3 rounded-full bg-[#F5F2EB] text-[#1A1814] font-medium hover:bg-[#F5F2EB]/90 transition-colors"
              data-testid="flowgrid-return-hub"
            >
              Return to Hub
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default FlowGrid;
